/**
 * Custom hook for fetching and managing IA Corner promos (highlights).
 * Handles GraphQL queries for IA Corner highlights/promos with horizontal pagination,
 * automatic population when insufficient items are available, and state management for loading and data.
 */

import Main from '@shared/types/enums/main';
import Slider from '@shared/classes/slider';
import info from '@shared/api/graphQL/iaCorners/info';
import parser from '@shared/api/graphQL/parser';
import query from '@shared/api/graphQL/iaCorners/promoQuery';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { IHighlightResponse } from '@shared/types/interfaces/slider';
import { SectionQueryParams } from '@shared/types/interfaces/query';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';

// Constants
/** Minimum number of highlights required before auto-population stops */
const MIN_HIGHLIGHTS = 4;
/** Number of highlights to fetch per page */
const HIGHLIGHT_PAGE_SIZE = 5;

/**
 * This interface defines the properties a graphql query request can receive
 */
interface Props {
  id: string;
  ready?: boolean;
  customPageIndex?: number;
  customPageSize?: number;
}

/**
 * Section hook. Fetches and returns highlights data.
 */
export default function useIACornerPromos({
  id,
  ready = true,
  customPageIndex,
  customPageSize,
}: Props): {
  /**
   * Whether the request is complete or not
   */
  complete: boolean;
  /**
   * Obtained highlights array
   */
  highlights: Slider[];
  /**
   * Horizontal and vertical pagination callback
   */
  onPagination: () => void;
} {
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const [complete, setComplete] = useState(
    !!currentParams?.rails || !!currentParams?.sliders || false
  );
  const [highlights, setHightlights] = useState(
    (currentParams as { sliders: Slider[] })?.sliders || []
  );
  const client = useApolloClient();
  const pagination = useRef({
    page: 0,
    totalPages: 0,
  });

  // Memoized params

  const params = useMemo(() => {
    return {
      horizontalParams: {
        page: customPageIndex || 0,
        pageSize: customPageSize || HIGHLIGHT_PAGE_SIZE,
      },
    };
  }, [customPageIndex, customPageSize]);

  // Event handlers

  /**
   * Request completion callback.
   */
  const onCompleted = useCallback(() => {
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Request error callback.
   */
  const onError = useCallback((error: ApolloError) => {
    // Print error
    console.error(error);
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Handles horizontal pagination for the specified content type.
   * Updates query variables to trigger data fetching.
   */
  const onPagination = useCallback(() => {
    /**
     * Set query variables so that new data is fetched
     */
    let newVariables: SectionQueryParams = {};
    // Horizontal highlight displacement
    const currentPage = pagination.current.page;
    const nextPage = currentPage + 1;
    const totalPages = pagination.current.totalPages;
    if (nextPage < totalPages) {
      pagination.current.page = Math.min(nextPage, totalPages);
      newVariables = {
        highlightsHorizontalParams: {
          page: pagination.current.page,
          pageSize: HIGHLIGHT_PAGE_SIZE,
        },
      };
    }
    // Halt callback
    else {
      return;
    }

    /**
     * Fetches the next set of results for a paginated list field
     */
    const normalizedVariables = {
      horizontalParams: newVariables.highlightsHorizontalParams,
    };
    const data = client.readQuery({
      query,
      variables: normalizedVariables,
    });
    if (!data) {
      fetchMore({
        variables: normalizedVariables,
      });
    }
  }, []);

  // Logic

  /**
   * Parses a given response and updates main contents
   */
  const updateContents = useCallback(
    (response: { highlights?: IHighlightResponse }) => {
      try {
        // Parse highlights
        const highlights = parser.highlights(response?.highlights);
        if (highlights) {
          // Update highlights
          setHightlights((state) => highlights?.data || state);
          pagination.current = {
            ...pagination.current,
            totalPages: highlights?.pages || 0,
          };

          // Populate highlights if required
          const shouldPopulate =
            (highlights?.data?.length || 0) < MIN_HIGHLIGHTS;
          shouldPopulate && onPagination();
        }
      } catch (e) {
        // Print error
        console.error(e);
      }
    },
    []
  );

  /**
   * Fetch highlights via graphql using the query hook
   */
  const { data, fetchMore } = useQuery(query, {
    skip: !ready,
    variables: {
      ...info(id),
      ...params,
    },
    onCompleted,
    onError,
  });

  /**
   * Updates data on change
   */
  useEffect(
    () =>
      updateContents({
        highlights: data?.highlights,
      }),
    [data]
  );

  return {
    complete,
    highlights,
    onPagination,
  };
}
